import classNames from "classnames"
import type Swiper from "swiper"
import { Autoplay, FreeMode, Navigation } from "swiper/modules"
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react"

import React, { PropsWithChildren, Children, useEffect, useState } from "react"

type Props = {
  sectionId: string
  cardWidth: "slim" | "normal" | "extra-slim"
  autoplay?: boolean
  centerCardsWhenTheyFit?: boolean
}

const MANUAL_SLIDE_SPEED = 300
const AUTOPLAY_SLIDE_SPEED = 5000

const Slider = ({
  sectionId,
  cardWidth,
  children,
  autoplay = false,
  centerCardsWhenTheyFit = false,
}: PropsWithChildren<Props>) => {
  const [swiperInstance, setSwiperInstance] = useState<Swiper | null>(null)
  const [cardsFitTheTrack, setCardsFitTheTrack] = useState(false)

  const centerCards = centerCardsWhenTheyFit && !autoplay && cardsFitTheTrack

  useEffect(() => {
    if (!swiperInstance) return

    const updateNavVisibility = () => {
      const prevButton = document.querySelector(
        `.swiper-prev-${sectionId}`
      ) as HTMLElement
      const nextButton = document.querySelector(
        `.swiper-next-${sectionId}`
      ) as HTMLElement

      if (prevButton && nextButton) {
        const isPrevDisabled = prevButton.classList.contains(
          "swiper-button-disabled"
        )
        const isNextDisabled = nextButton.classList.contains(
          "swiper-button-disabled"
        )

        prevButton.style.display =
          isPrevDisabled && isNextDisabled ? "none" : "block"
        nextButton.style.display =
          isPrevDisabled && isNextDisabled ? "none" : "block"
      }
    }

    swiperInstance.on("resize", updateNavVisibility)

    if (centerCardsWhenTheyFit) {
      swiperInstance.on("lock", updateNavVisibility)
      swiperInstance.on("unlock", updateNavVisibility)
    }

    updateNavVisibility()

    return () => {
      swiperInstance.off("resize", updateNavVisibility)
      swiperInstance.off("lock", updateNavVisibility)
      swiperInstance.off("unlock", updateNavVisibility)
    }
  }, [swiperInstance, sectionId, centerCardsWhenTheyFit])

  useEffect(() => {
    if (!swiperInstance || !autoplay) return

    const prevButton = document.querySelector<HTMLElement>(
      `.swiper-prev-${sectionId}`
    )
    const nextButton = document.querySelector<HTMLElement>(
      `.swiper-next-${sectionId}`
    )

    if (!prevButton || !nextButton) return

    const slide = (direction: "next" | "prev") => () => {
      // The marquee is permanently mid-transition, so hand control over to the
      // user first: stop autoplay, land the running transition and release
      // Swiper's animating lock, which would otherwise swallow the slide.
      swiperInstance.autoplay.stop()
      swiperInstance.slideTo(swiperInstance.activeIndex, 0, false)
      swiperInstance.animating = false

      // Then carry out the click the user actually made.
      if (direction === "next") {
        swiperInstance.slideNext(MANUAL_SLIDE_SPEED)
      } else {
        swiperInstance.slidePrev(MANUAL_SLIDE_SPEED)
      }
    }

    const onNextClick = slide("next")
    const onPrevClick = slide("prev")

    nextButton.addEventListener("click", onNextClick)
    prevButton.addEventListener("click", onPrevClick)

    return () => {
      nextButton.removeEventListener("click", onNextClick)
      prevButton.removeEventListener("click", onPrevClick)
    }
  }, [swiperInstance, sectionId, autoplay])

  useEffect(() => {
    if (!swiperInstance || !centerCardsWhenTheyFit || autoplay) return

    const measureCardsAgainstTrack = () => {
      const { slides, params, width, el: sliderElement } = swiperInstance

      if (!slides.length) return

      const gapBetweenCards = Number(params.spaceBetween) || 0
      const widthOfAllCards =
        slides.reduce((total, slide) => total + slide.offsetWidth, 0) +
        gapBetweenCards * (slides.length - 1)
      const bleedPastTheGutter =
        Number.parseFloat(getComputedStyle(sliderElement).marginRight) || 0

      setCardsFitTheTrack(widthOfAllCards <= width + bleedPastTheGutter)
    }

    measureCardsAgainstTrack()
    swiperInstance.on("resize", measureCardsAgainstTrack)

    return () => {
      swiperInstance.off("resize", measureCardsAgainstTrack)
    }
  }, [swiperInstance, centerCardsWhenTheyFit, autoplay])

  useEffect(() => {
    if (!swiperInstance || !centerCardsWhenTheyFit || autoplay) return

    swiperInstance.update()
  }, [swiperInstance, centerCardsWhenTheyFit, autoplay, centerCards])

  return (
    <SwiperComponent
      modules={[FreeMode, Navigation, Autoplay]}
      spaceBetween={20}
      slidesPerView="auto"
      freeMode
      grabCursor
      className={classNames(
        "xl:!pr-0",
        {
          "-mr-gutter-xs sm:-mr-gutter-sm md:-mr-gutter-md lg:-mr-gutter-lg xl:-mr-gutter-xl 4k:-mr-gutter-4k":
            !centerCards,
        },
        { "[&>.swiper-wrapper]:!ease-linear": autoplay }
      )}
      centerInsufficientSlides={centerCards}
      navigation={
        autoplay
          ? false
          : {
              nextEl: `.swiper-next-${sectionId}`,
              prevEl: `.swiper-prev-${sectionId}`,
            }
      }
      watchOverflow
      breakpoints={{
        900: {
          spaceBetween: 24,
        },
        1200: {
          spaceBetween: 32,
        },
      }}
      onSwiper={setSwiperInstance}
      speed={autoplay ? AUTOPLAY_SLIDE_SPEED : MANUAL_SLIDE_SPEED}
      loop={autoplay}
      loopPreventsSliding={false}
      autoplay={autoplay ? { delay: 0 } : false}
    >
      {/* TODO Note: the numbers below are calculated based on number of visible cards + the gap between them alongside the gutter to calculate the margin right of last card
       * this should be revisisted with swiper update to see if better way to handle this
       */}
      {Children.map(children, (child, index) => (
        <SwiperSlide
          key={`swiper-slide-${index}`}
          className={classNames(
            "grid h-auto w-[280px]",
            {
              "last:mr-[calc(100vw-5.33vw-280px)] last:sm:mr-[calc(100vw-4.17vw-580px)] md:w-[390px] last:md:mr-[calc(100vw-4.17vw-810px)] lg:w-[495px] last:lg:mr-[calc(100vw-7.22vw-1026px)] last:xl:mr-[calc(100vw-9.76vw-1024px)] last:4k:mr-0":
                cardWidth === "normal",
            },
            {
              "last:mr-[calc(100vw-5.33vw-280px)] last:sm:mr-[calc(100vw-4.17vw-580px)] md:w-[340px] last:md:mr-[calc(100vw-4.17vw-710px)] lg:w-[390px] last:lg:mr-[calc(100vw-7.22vw-1234px)] last:xl:mr-[calc(100vw-9.76vw-1234px)] last:4k:mr-0":
                cardWidth === "slim",
            },
            {
              "last:mr-[calc(100vw-5.33vw-260px)] last:sm:mr-[calc(100vw-4.17vw-540px)] md:w-[270px] last:md:mr-[calc(100vw-4.17vw-570px)] lg:w-[320px] last:lg:mr-[calc(100vw-7.22vw-894px)] last:xl:mr-[calc(100vw-9.76vw-894px)] last:4k:mr-0":
                cardWidth === "extra-slim",
            },
            { "last:!mr-0": centerCards }
          )}
        >
          {child}
        </SwiperSlide>
      ))}
    </SwiperComponent>
  )
}
export default Slider
